<template>
  <IbtRegisterForm
    :title="$t('verificationStatus.ibtVerification.confirmId.title')"
    :desc="$t('verificationStatus.ibtVerification.confirmId.desc')"
    :nextButtonOnClick="nextButtonOnClick"
    :backButtonOnClick="backButtonOnClick"
  >
    <div class="mt-10 flex flex-col items-stretch justify-start">
      <IdInformationComponent
        ref="infoForm"
        radioLabel="verificationStatus.ibtVerification.confirmId.idTypeLabel"
        :idTypeOptions="idTypeOptions"
        :disabled="isInfoDisabled"
      />
      <IdUploadComponent
        ref="uploadForm"
        title="verificationStatus.ibtVerification.confirmId.uploadLabel"
        :uploadInfoText="$t('verificationStatus.ibtVerification.confirmId.uploadInfoText')"
        :pendingReason="pendingReason"
        :isBackImageRequired="isBackImageRequired"
      />
    </div>
  </IbtRegisterForm>
</template>

<script>
import { mapGetters, mapState } from 'vuex';
import { urlToFile } from '@/util/convertToFile';
import { endAjaxLoading, startLoading } from '@/util/loading';
import auditStatus from '@/constants/auditStatus';
import ibtStep from '@/constants/register/ibtStep';
import fileType from '@/constants/fileType';
import auditType from '@/constants/register/auditType';
import registerRepository from '@/repositories/register/registerRepository';
import commonRepository from '@/repositories/commonRepository';
import ibtStore from '@/store/register/ibtVerification/ibtStore';
import IdInformationComponent, { idType } from '@/components/register/registerForms/uploadForms/IdInformation.vue';
import IdUploadComponent from '@/components/register/registerForms/uploadForms/IdUpload.vue';
import IbtRegisterForm from '@/components/register/registerForms/IbtRegisterForm.vue';
import { sensorsTrack } from '@/util/sensorsdata';
import { SENSORS_TRACK } from '@/util/sensorsdata/constant';

export default {
  name: 'ConfirmId',
  components: { IdInformationComponent, IdUploadComponent, IbtRegisterForm },
  data() {
    return { isBackImageRequired: true };
  },
  created() {
    this.idTypeOptions = [
      {
        value: idType.id,
        label: 'verificationStatus.idInformation.idCard'
      },
      {
        value: idType.passport,
        label: 'verificationStatus.idInformation.passport'
      }
    ];
  },
  computed: {
    isInfoDisabled() {
      return this.ibtPoiAuditStatus === auditStatus.pending;
    },
    isPrefillGeneralUpload() {
      // Only prefill when init
      return this.ibtPoiAuditStatus === auditStatus.init;
    },
    pendingReason() {
      return this.ibtPoiAuditStatus === auditStatus.pending
        ? ibtStore.state.poiCustomPendingReason?.length > 0
          ? ibtStore.state.poiCustomPendingReason
          : ibtStore.state.poiPendingReason?.length > 0
          ? ibtStore.state.poiPendingReason
          : ''
        : '';
    },
    poiRecordId() {
      return ibtStore.state.poiRecordId;
    },
    // Pending record
    poiPendingRecord() {
      return ibtStore.state.poiPendingRecord;
    },
    prevExistingIdAdvancedFilePathList() {
      return ibtStore.state.prevExistingIdAdvancedFilePathList;
    },
    ...mapState('common', ['ibtPoiAuditStatus', 'ibtPoaAuditStatus']),
    ...mapGetters('common', ['isIbtPoaEditable'])
  },
  mounted() {
    // To watch refs' value
    this.$watch('$refs.infoForm.form.idType', currIdType => {
      this.isBackImageRequired = currIdType !== idType.passport;

      this.$nextTick(() => {
        this.$refs.uploadForm?.clearValidate();
      });
    });
    this.init();
    sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_VIEW, {
      openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV2
    })
  },
  methods: {
    async init() {
      if (this.ibtPoiAuditStatus === auditStatus.pending) {
        const response = await ibtStore.dispatch('actionGetData');
        if (response instanceof Error) {
          this.$message({
            message: response.message,
            type: 'error'
          });
        }

        if (this.$refs.infoForm?.form) {
          this.$refs.infoForm.form = {
            ...this.$refs.infoForm.form,
            ...this.poiPendingRecord
          };
        }
      } else {
        const response = await registerRepository.getData(5);

        if (response instanceof Error) {
          this.$message({
            message: response.message,
            type: 'error'
          });
        } else {
          const { firstName, middleName, lastName, nationalityId, countryId, idType, idNumber, idDocFilePathList } = response;

          const isPrevIdTypeIncluded = idType && this.idTypeOptions.map(e => e.value).includes(idType);

          const idInformationForm = {
            ...(firstName && { firstName }),
            ...(middleName && { middleName }),
            ...(lastName && { lastName }),
            ...(nationalityId && { nationalityId }),
            ...(countryId && { countryId }),
            // prefill only if idType is in the options
            ...(isPrevIdTypeIncluded && {
              idType
            }),
            ...(isPrevIdTypeIncluded && idNumber && { idNumber })
          };

          if (this.$refs.infoForm?.form) {
            this.$refs.infoForm.form = {
              ...this.$refs.infoForm.form,
              ...idInformationForm
            };
          }

          if (this.isPrefillGeneralUpload && this.$refs.uploadForm?.form && idDocFilePathList?.length > 0) {
            const prefillFilePathList =
              idDocFilePathList.length === 1 ? idDocFilePathList.slice(-1) : idDocFilePathList.slice(-2);

            this.$refs.uploadForm.form = {
              ...this.$refs.uploadForm.form,
              ...(prefillFilePathList?.[0] && { frontImage: await urlToFile(`/api${prefillFilePathList[0]}`) }),
              ...(prefillFilePathList?.[1] && { backImage: await urlToFile(`/api${prefillFilePathList[1]}`) })
            };
            this.$nextTick(() => this.$refs.uploadForm.clearValidate());
          }
        }
      }
    },
    backButtonOnClick() {
      sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_CLICK, {
        openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV2,
        openidentity_step: SENSORS_TRACK.IDENTITY_STEP.LV2_ID_INFORMATION,
        button_name: SENSORS_TRACK.BUTTON_NAME.LV2_BACK
      })
      ibtStore.dispatch('setActiveTab', ibtStep.financialInformation);
    },
    async submitIdForm() {
      const infoForm = this.$refs.infoForm.form;
      const uploadForm = this.$refs.uploadForm.form;

      const { frontImage, backImage } = uploadForm;
      startLoading('ajax');

      const uploadImagesResponses = await Promise.all([
        frontImage !== null ? commonRepository.uploadFile(frontImage) : null,
        backImage !== null ? commonRepository.uploadFile(backImage) : null
      ]);

      const frontImageResponse = uploadImagesResponses?.[0] ?? null;
      const backImageResponse = uploadImagesResponses?.[1] ?? null;

      if (frontImageResponse instanceof Error) {
        this.$message({
          message: frontImageResponse.message,
          type: 'error'
        });
      } else if (backImageResponse instanceof Error) {
        this.$message({
          message: backImageResponse.message,
          type: 'error'
        });
      } else {
        const updateInfoResponse = this.isInfoDisabled ? null : await registerRepository.updateUserIdAddress(infoForm);
        if (updateInfoResponse instanceof Error) {
          this.$message({
            message: updateInfoResponse.message,
            type: 'error'
          });
        } else {
          const verificationResponse = await registerRepository.sendVerification({
            filePathList: [
              ...(frontImageResponse !== null ? [frontImageResponse] : []),
              ...(backImageResponse !== null ? [backImageResponse] : [])
            ],
            fileType: fileType.poi,
            auditType: auditType.ibt,
            fileId: this.poiRecordId,
            isPending: this.ibtPoiAuditStatus === auditStatus.pending,
            prevExistingFilePathList: this.prevExistingIdAdvancedFilePathList
          });

          if (verificationResponse instanceof Error) {
            this.$message({
              message: verificationResponse.message,
              type: 'error'
            });
          } else {
            this.$store.commit('common/setIbtPoiAuditStatus', auditStatus.submitted);
            ibtStore.dispatch('setStep', ibtStep.ibtStatus);
          }
        }
      }

      endAjaxLoading();
    },
    async nextButtonOnClick() {
      sensorsTrack(SENSORS_TRACK.EVENT_NAME.OPEN_IDENTITY_PAGE_CLICK, {
        openidentity_level: SENSORS_TRACK.IDENTITY_LEVEL.LV2,
        openidentity_step: SENSORS_TRACK.IDENTITY_STEP.LV2_ID_INFORMATION,
        button_name: SENSORS_TRACK.BUTTON_NAME.LV2_NEXT
      })
      const valids = await Promise.all([
        new Promise(resolve => this.$refs.infoForm?.validate(valid => resolve(valid))),
        new Promise(resolve => this.$refs.uploadForm?.validate(valid => resolve(valid)))
      ]);
      const isInfoFormValid = valids[0];
      const isUploadFormValid = valids[1];

      if (isInfoFormValid && isUploadFormValid) {
        if (!this.isIbtPoaEditable) {
          // Only re-submit IdForm
          this.submitIdForm();
        } else {
          ibtStore.dispatch('setIdInfoForm', this.isInfoDisabled ? null : this.$refs.infoForm.form);
          ibtStore.dispatch('setIdUploadForm', this.$refs.uploadForm.form);
          ibtStore.dispatch('setStep', ibtStep.confirmPoa);
        }
      }
    }
  }
};
</script>
